// src/components/molecules/ItemsPerPageSelector/ItemsPerPageSelector.jsx
import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Button, Menu, MenuItem, Tooltip } from '@mui/material';
import { ViewComfy as ViewComfyIcon } from '@mui/icons-material';
import {
  operationalFontSizes,
  operationalRadii,
  operationalSizing,
  operationalSpacing
} from '../../../design-system/tokens';

/**
 * ItemsPerPageSelector - Componente molecular para seleção de itens por página
 *
 * CLASSIFICAÇÃO: MOLÉCULA
 *
 * Este componente combina Button + Tooltip + Menu + MenuItems para criar
 * um seletor de quantidade de itens por página com menu dropdown.
 *
 * @see https://bradfrost.com/blog/post/atomic-web-design/
 */
const ItemsPerPageSelector = ({
  value,
  onChange,
  options = [12, 24, 36, 48, -1],
  optionLabels = {
    '-1': 'Todos'
  },
  className = '',
  ...props
}) => {
  const [anchorEl, setAnchorEl] = useState(null);
  
  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };
  
  const handleClose = () => {
    setAnchorEl(null);
  };
  
  const handleSelect = (optionValue) => {
    onChange(optionValue);
    handleClose();
    
    // Salvar a preferência no localStorage
    localStorage.setItem('atomic_cardsPerPage', optionValue);
  };
  
  const getOptionLabel = (option) => {
    if (optionLabels[option.toString()]) {
      return optionLabels[option.toString()];
    }
    return `${option} por página`;
  };
  
  return (
    <>
      <Tooltip title="Itens por página" arrow>
        <Button
          variant="outlined"
          onClick={handleClick}
          className={`items-per-page-selector ${className}`}
          sx={{
            // Usando Design Tokens para consistência
            minWidth: operationalSizing.compact,
            height: operationalSizing.compact,
            px: operationalSpacing.md,
            py: operationalSpacing.xs,
            fontSize: operationalFontSizes.lg,
            borderRadius: `${operationalRadii.control}px`,
            ...props.sx
          }}
          {...props}
        >
          <ViewComfyIcon />
        </Button>
      </Tooltip>
      
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
      >
        {options.map((option) => (
          <MenuItem
            key={option}
            selected={value === option}
            onClick={() => handleSelect(option)}
            sx={value === option ? { color: 'primary.main', fontWeight: 600 } : {}}
          >
            {getOptionLabel(option)}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};

ItemsPerPageSelector.propTypes = {
  value: PropTypes.number.isRequired,
  onChange: PropTypes.func.isRequired,
  options: PropTypes.arrayOf(PropTypes.number),
  optionLabels: PropTypes.object,
  className: PropTypes.string
};

export default ItemsPerPageSelector;
